import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { clsx } from "clsx";
import { ArrowRight, Loader2, FileText, RotateCcw, AlertTriangle, Star, ChevronUp, ChevronDown, X, Plus, XCircle, ExternalLink } from "lucide-react";
import type { ApparelStudy } from "@erc/shared";
import { authHeaders, getBackendUrl } from "../../lib/http";
import { apiClient } from "../../lib/api";
import { customApi, studioTestDesigns, type CustomListingDetail } from "../../lib/customListing";
import { MockupChooserModal, mockupHasArea, type ChooserMockup } from "../mockup/MockupChooserModal";
import { Lightbox } from "./EtsyPreview";
import { NicheFamily } from "./NicheFamily";
import { SampleOrder } from "./SampleOrder";

// Custom Liste Studyosu 6. adim: liste — Liste Denetimi'ndeki satir duzeninde.
// Sablon ve mockup grubu secilir, engel/uyari listesi gorunur, gorseller suruklenerek
// siralanir (ilk sira ana gorsel), "Mockup ekle" ile yapiya uyan mockuplar orneklerle
// basilip eklenir. "Taslaga cevir" panel taslagini galerideki sirayla kurar; liste
// sablonunun gorselleri (beden/renk tablosu) sona eklenir. Etsy gorunumu 7. adimda.

interface TemplateRow {
  id: string; name: string; platform?: string | null; price?: number | null; imageUrls?: string[];
  taxonomyId?: number | null; shippingProfileId?: string | null;
  variations?: Array<{ name?: string; options?: Array<{ label?: string; sellingPrice?: number | null; priceAdjust?: number | null }> }> | null;
}
const abs = (u: string) => (/^(https?:|data:)/.test(u) ? u : `${getBackendUrl()}${u.startsWith("/") ? "" : "/"}${u}`);
type Issue = { code: string; message: string };

export function ListingStep({ rec, onChanged }: { rec: CustomListingDetail; onChanged: () => void }) {
  const study = rec.study as ApparelStudy | null;
  const { data: templates = [] } = useQuery({
    queryKey: ["listing-templates"],
    queryFn: async () => {
      const r = await fetch(`${getBackendUrl()}/api/listing-templates`, { headers: authHeaders() });
      const j = await r.json().catch(() => ({}));
      return (j.data ?? []) as TemplateRow[];
    },
  });
  const { data: collections = [] } = useQuery({ queryKey: ["mockup-collections", "mockup"], queryFn: () => apiClient.listMockupCollections("mockup") });
  const [tplId, setTplId] = useState(rec.listing.listingTemplateId ?? "");
  const tpl = templates.find((t) => t.id === tplId);
  const [images, setImages] = useState<string[]>(rec.listing.images ?? []);
  useEffect(() => { setImages(rec.listing.images ?? []); }, [rec.listing.images?.join("|")]);
  const [picker, setPicker] = useState(false);
  const [open, setOpen] = useState(false);
  const [drag, setDrag] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [withVideo, setWithVideo] = useState(true);
  const [zoom, setZoom] = useState<number | null>(null);

  const saveTpl = useMutation({ mutationFn: (v: string) => customApi("PATCH", `/${rec.id}`, { listing: { listingTemplateId: v } }), onSuccess: onChanged });
  const saveImages = useMutation({
    mutationFn: (next: string[]) => customApi("PATCH", `/${rec.id}`, { listing: { images: next } }),
    onSuccess: onChanged, onError: (e) => setErr((e as Error).message),
  });
  const setOrder = (next: string[]) => { setImages(next); saveImages.mutate(next); };
  const addGroup = useMutation({
    // Grup degisince eski grubun gorselleri cikar, yenisininkiler eklenir ("" = grubu kaldir)
    mutationFn: (collectionId: string) => customApi<{ added: number; skipped?: number }>("POST", `/${rec.id}/images`, { collectionId, reserve: tplImages.length }),
    onSuccess: (r) => { setErr(r.skipped ? `Galeride yer kalmadığı için gruptaki ${r.skipped} mockup eklenmedi.` : null); onChanged(); },
    onError: (e) => setErr((e as Error).message),
  });
  const rerender = useMutation({
    mutationFn: () => customApi<{ updated: number }>("POST", `/${rec.id}/rerender`),
    onSuccess: (r) => { setErr(null); onChanged(); setNote(`${r.updated} mockup güncel ayarlarla yeniden basıldı.`); },
    onError: (e) => setErr((e as Error).message),
  });
  const [note, setNote] = useState<string | null>(null);
  const build = useMutation({
    mutationFn: (force: boolean) => customApi<{ draftId: string; created: boolean }>("POST", `/${rec.id}/draft`, { force, video: withVideo }),
    onSuccess: () => { setErr(null); onChanged(); }, onError: (e) => setErr((e as Error).message),
  });

  const tplImages = (tpl?.imageUrls ?? []).filter((u) => !u.startsWith("data:")).map(abs);
  const groupId = rec.listing.group?.id ?? "";
  const total = images.length + tplImages.length;
  const axes = (tpl?.variations ?? []).filter((v) => (v.options?.length ?? 0) > 0);
  const combos = axes.reduce((n, v) => n * (v.options?.length ?? 1), axes.length ? 1 : 0);
  const prices = axes.flatMap((v) => (v.options ?? []).map((o) => o.sellingPrice).filter((x): x is number => typeof x === "number"));
  const price = prices.length ? Math.min(...prices) : tpl?.price ?? null;
  const seo = rec.analysis?.seo;
  const thumb = rec.template.samples?.renders?.[0]?.url ?? images[0] ?? rec.analysis?.imageUrls?.[0];

  // Engeller / uyarilar (Liste Denetimi kurallari + studyo adimlari)
  const blockers: Issue[] = [
    ...(study?.status === "approved" ? [] : [{ code: "no_study", message: "Liste çalışması onaylı değil (2. adım)" }]),
    ...((rec.template.samples?.renders?.length ?? 0) ? [] : [{ code: "no_samples", message: "Örnek basılmadı (4. adım)" }]),
    ...(tplId ? [] : [{ code: "no_template", message: "Liste şablonu seçilmedi" }]),
    ...(tplId && tpl && !tpl.taxonomyId ? [{ code: "no_taxonomy", message: "Şablonda Etsy kategorisi yok; liste yanlış kategoriye açılır" }] : []),
    ...(tplId && tpl && !tpl.shippingProfileId ? [{ code: "no_shipping", message: "Şablonda kargo profili yok; Etsy yayını reddeder" }] : []),
    ...(tplId && tpl && price == null ? [{ code: "no_price", message: "Şablonda fiyat yok" }] : []),
    ...(axes.length > 3 ? [{ code: "too_many_axes", message: `${axes.length} varyasyon ekseni; Etsy en fazla 3 kabul ediyor` }] : []),
    ...(images.length ? [] : [{ code: "no_images", message: "Görsel yok: kolaj oluştur ya da mockup ekle" }]),
    ...(total > 20 ? [{ code: "too_many_images", message: `${images.length} görsel + ${tplImages.length} şablon görseli = ${total}; Etsy en fazla 20 kabul ediyor` }] : []),
    ...(study && !study.questions.length && study.layers.some((l) => l.source !== "fixed") ? [{ code: "custom_without_personalization", message: "Custom ürün ama kişiselleştirme sorusu yok" }] : []),
  ];
  const warnings: Issue[] = [
    ...(seo?.tags?.length ? [] : [{ code: "no_tags", message: "Etiket yok (Etsy 13 etikete kadar izin veriyor)" }]),
    ...(study?.risk && study.risk.level !== "low" ? [{ code: "copyright", message: `Telif riski: ${study.risk.level === "high" ? "yüksek" : "orta"}${study.risk.reasonsTr[0] ? `; ${study.risk.reasonsTr[0]}` : ""}` }] : []),
    ...(rec.links.draftId ? [{ code: "already_listed", message: "Bu üründen zaten bir panel taslağı var; yeniden kurmak yeni taslak açar" }] : []),
    ...(images.length && images.length < 5 ? [{ code: "few_images", message: `${images.length} görsel; Etsy'de 5 ve üzeri önerilir` }] : []),
  ];
  const status = blockers.length ? "Engel" : warnings.length ? "Uyarı" : "Hazır";
  const statusCls = blockers.length ? "bg-red-50 text-red-700" : warnings.length ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700";
  const onDrop = (to: number) => {
    if (drag === null || drag === to) return;
    const next = [...images];
    const [m] = next.splice(drag, 1);
    next.splice(to, 0, m);
    setDrag(null);
    setOrder(next);
  };

  return (
    <div className="space-y-4">
      <div className={clsx("rounded-xl border bg-white p-3", blockers.length ? "border-red-200" : "border-emerald-200")}>
        <div className="flex gap-3">
          <div className="w-28 flex-shrink-0">
            {thumb ? <img src={thumb} alt="" className="h-28 w-28 rounded-lg border border-gray-200 bg-gray-50 object-contain" /> : <div className="h-28 w-28 rounded-lg bg-gray-100" />}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="line-clamp-2 text-sm font-semibold text-gray-900">{seo?.title ?? rec.name}</p>
                <p className="text-xs text-gray-500">{rec.analysis?.productType ?? "custom"}{price != null ? ` · $${Number(price).toFixed(2)}` : ""}</p>
              </div>
              <span className={clsx("flex-shrink-0 rounded-full px-2 py-0.5 text-xs font-medium", statusCls)}>{status}</span>
            </div>

            <div className="mt-2 grid gap-2 sm:grid-cols-2">
              <div>
                <label htmlFor="cl-template" className="mb-0.5 block text-xs text-gray-500">Liste şablonu</label>
                <select id="cl-template" value={tplId} onChange={(e) => { setTplId(e.target.value); saveTpl.mutate(e.target.value); }}
                  className={clsx("w-full rounded-lg border px-2 py-1.5 text-sm focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-200", tplId ? "border-gray-200" : "border-red-300 bg-red-50")}>
                  <option value="">Seç…</option>
                  {templates.filter((t) => !t.platform || t.platform === "etsy").map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="cl-group" className="mb-0.5 block text-xs text-gray-500">Mockup grubu (seçince mockup görselleri bu grubunkilerle değişir; kolaj ve kartelalar kalır)</label>
                <select id="cl-group" value={groupId} disabled={addGroup.isPending} onChange={(e) => addGroup.mutate(e.target.value)}
                  className="w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-200">
                  <option value="">{addGroup.isPending ? "Basılıyor…" : groupId ? "Grubu ve mockupları kaldır" : "Grup seç…"}</option>
                  {collections.filter((c) => !c.productTypeKey || /shirt|tee|sweat|hood|tank|apparel|baby/i.test(c.productTypeKey) === (rec.isGarment !== false)).map((c) => <option key={c.id} value={c.id}>{c.name} · {c.mockups?.length ?? 0}</option>)}
                </select>
                
              </div>
            </div>

            <div className="mt-2 flex flex-wrap gap-1.5 text-xs">
              <span className={clsx("rounded-full px-2 py-0.5", total > 20 ? "bg-red-50 text-red-700" : "bg-gray-100 text-gray-600")}>Görsel {total}/20 ({images.length} görsel + {tplImages.length} şablon)</span>
              {axes.length > 0 && <span className="rounded-full bg-gray-100 px-2 py-0.5 text-gray-600">{axes.map((a) => a.name).join(" × ")} · {combos} kombinasyon</span>}
              <span className={clsx("rounded-full px-2 py-0.5", images.length ? "bg-amber-50 text-amber-700" : "bg-gray-100 text-gray-500")}>{images.length ? "Ana görsel: 1. sıra" : "Ana görsel yok"}</span>
              {(rec.listing.bigPrintUrls ?? []).some((u) => images.includes(abs(u))) && (
                <span className="rounded-full bg-orange-50 px-2 py-0.5 font-medium text-orange-700" title="Galeride big print mockup var: fiyata +$5 eklenir, taslak big print olur (sipariş dosyası 16 inç)">Big print · fiyat +$5</span>
              )}
              {study?.risk && <span className={clsx("rounded-full px-2 py-0.5", study.risk.level === "high" ? "bg-red-50 text-red-700" : study.risk.level === "medium" ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700")}>telif: {study.risk.level === "high" ? "yüksek" : study.risk.level === "medium" ? "orta" : "düşük"}</span>}
              {rec.analysis?.url && <a href={rec.analysis.url.split("#")[0]} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-indigo-600 hover:underline">İlham listesi <ExternalLink className="h-3 w-3" /></a>}
            </div>

            {(blockers.length > 0 || warnings.length > 0) && (
              <ul className="mt-2 space-y-0.5">
                {blockers.map((b) => <li key={b.code} className="flex items-start gap-1 text-xs text-red-700"><XCircle className="mt-0.5 h-3 w-3 flex-shrink-0" />{b.message}</li>)}
                {warnings.map((w) => <li key={w.code} className="flex items-start gap-1 text-xs text-amber-700"><AlertTriangle className="mt-0.5 h-3 w-3 flex-shrink-0" />{w.message}</li>)}
              </ul>
            )}
          </div>
        </div>

        {/* Gorsel seridi */}
        <div className="mt-3">
          <div className="mb-1.5 flex flex-wrap items-center gap-2">
            <p className="text-xs font-medium text-gray-600">Görseller ({images.length}) · sürükleyerek sırala, ilk sıra ana görsel</p>
            <button onClick={() => rerender.mutate()} disabled={rerender.isPending} title="Mockup ayarlarını (alan, maske, efekt) değiştirdiysen galerideki mockupları güncel ayarlarla yeniden basar; sıra korunur"
              className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-2 py-0.5 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50">
              {rerender.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />} Mockupları yeniden bas
            </button>
            {note && <span className="text-xs text-emerald-700">{note}</span>}
          </div>
          <div className="grid grid-cols-4 gap-2.5 sm:grid-cols-6 xl:grid-cols-8">
            {images.map((u, i) => (
              <div key={u} draggable onDragStart={() => setDrag(i)} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(i)}
                className={clsx("group relative cursor-move overflow-hidden rounded-lg border-2", i === 0 ? "border-amber-400 ring-2 ring-amber-100" : "border-gray-200", drag === i && "opacity-50")}>
                <img src={u} alt="" loading="lazy" onClick={() => setZoom(i)} title="Büyüt" className="aspect-square w-full cursor-zoom-in bg-gray-50 object-cover" />
                <span className={clsx("absolute left-1 top-1 rounded-full px-1.5 text-xs font-semibold", i === 0 ? "bg-amber-400 text-white" : "bg-black/60 text-white")}>{i === 0 ? "★1" : i + 1}</span>
                <div className="absolute right-1 top-1 flex gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
                  {i > 0 && <button onClick={() => setOrder([images[i], ...images.filter((_, k) => k !== i)])} title="Ana görsel yap" aria-label="Ana görsel yap" className="rounded-full bg-white/90 p-1 text-amber-600 shadow"><Star className="h-3.5 w-3.5" /></button>}
                  <button onClick={() => setOrder(images.filter((_, k) => k !== i))} title="Listeden çıkar" aria-label="Listeden çıkar" className="rounded-full bg-white/90 p-1 text-red-600 shadow"><X className="h-3.5 w-3.5" /></button>
                </div>
              </div>
            ))}
            <button onClick={() => setPicker(true)} disabled={total >= 20} title={total >= 20 ? "Galeri dolu (20/20); önce görsel çıkar" : undefined}
              className="flex aspect-square flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-gray-300 text-xs text-gray-500 hover:border-indigo-300 hover:text-indigo-600 disabled:opacity-50">
              <Plus className="h-5 w-5" /> Mockup ekle
            </button>
            {tplImages.map((u) => (
              <div key={u} title="Liste şablonundan; sona eklenir" className="relative overflow-hidden rounded-lg border-2 border-dashed border-gray-200 opacity-60">
                <img src={u} alt="" loading="lazy" className="aspect-square w-full object-cover" />
                <span className="absolute bottom-1 left-1 rounded-full bg-white/90 px-1.5 text-xs text-gray-600">şablon</span>
              </div>
            ))}
          </div>
        </div>

        {/* Tasarimlar: sira mockuplara denk gelen tasarimi belirler */}
        <div className="mt-3">
          <SampleOrder rec={rec} onChanged={() => { setErr(null); onChanged(); }} onError={setErr}
            hint={rec.listing.group?.id ? " (grup mockupları yeniden basılır)" : ""}
            // Grup secili: mockup gorselleri yeni sirayla yeniden basilir
            afterSave={rec.listing.group?.id ? () => customApi("POST", `/${rec.id}/images`, { collectionId: rec.listing.group!.id, reserve: tplImages.length }) : undefined} />
        </div>

        {/* Olusacak liste yapisi */}
        <button onClick={() => setOpen((o) => !o)} aria-expanded={open} className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-indigo-600">
          {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />} Oluşacak liste yapısı
        </button>
        {open && (
          <dl className="mt-2 space-y-1 rounded-lg border border-gray-200 p-3 text-xs">
            <div><dt className="inline font-medium text-gray-700">Başlık: </dt><dd className="inline text-gray-600">{seo?.title ?? "—"}</dd></div>
            <div><dt className="inline font-medium text-gray-700">Açıklama: </dt><dd className="inline whitespace-pre-line text-gray-600">{(seo?.description ?? "").slice(0, 300) || "—"}</dd></div>
            <div><dt className="inline font-medium text-gray-700">Etiket ({seo?.tags?.length ?? 0}): </dt><dd className="inline text-gray-600">{seo?.tags?.join(", ") || "—"}</dd></div>
            <div><dt className="inline font-medium text-gray-700">Varyasyon: </dt><dd className="inline text-gray-600">{axes.length ? `${axes.map((a) => a.name).join(" × ")} (${combos} kombinasyon)` : "—"}</dd></div>
            <div><dt className="inline font-medium text-gray-700">Kişiselleştirme ({study?.questions.length ?? 0}): </dt><dd className="inline text-gray-600">{study?.questions.map((q) => q.question).join(" · ") || "—"}</dd></div>
          </dl>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {rec.links.draftId && (
          <Link to={`/listing-builder?id=${rec.links.draftId}`} className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
            Taslağı aç <ArrowRight className="h-4 w-4" />
          </Link>
        )}
        <button onClick={() => { if (!rec.links.draftId || confirm("Bu görsellerle yeni bir panel taslağı kurulsun mu? Eski taslak silinmez.")) build.mutate(!!rec.links.draftId); }}
          disabled={build.isPending || blockers.length > 0}
          className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:bg-indigo-300">
          {build.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : rec.links.draftId ? <RotateCcw className="h-4 w-4" /> : <FileText className="h-4 w-4" />}
          {rec.links.draftId ? "Yeniden taslağa çevir" : "Taslağa çevir"}
        </button>
        <label className="inline-flex items-center gap-1.5 text-xs text-gray-600" title="Liste görsellerinden 8 sn'lik slayt videosu (kartelalar hariç, ilk 6 görsel); taslakla Etsy'ye gider">
          <input type="checkbox" checked={withVideo} onChange={(e) => setWithVideo(e.target.checked)} className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-200" />
          Video ekle (kartelalar hariç)
        </label>
        <span className="text-xs text-gray-500">Etsy'ye gönderilmez; Etsy'deki görünümü 7. adımda kontrol edersin.</span>
      </div>
      {err && <p className="text-sm text-red-600">{err}</p>}

      {rec.links.draftId && <NicheFamily rec={rec} onChanged={onChanged} />}
      {zoom !== null && <Lightbox images={images} index={zoom} onClose={() => setZoom(null)} />}
      {picker && <MockupPicker rec={rec} reserve={tplImages.length} onClose={() => setPicker(false)} onAdded={onChanged} />}
    </div>
  );
}

// "Mockup ekle": Liste Denetimi ile AYNI pencere (tum havuz, arama, klasor, alan
// belirleme). Tiklanan mockup orneklerle basilip galerinin sonuna eklenir; pencere acik kalir.
function MockupPicker({ rec, reserve, onClose, onAdded }: { rec: CustomListingDetail; reserve: number; onClose: () => void; onAdded: () => void }) {
  const [added, setAdded] = useState<string[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const study = rec.study as ApparelStudy | null;
  const insp = study?.imageUrls?.[0] ?? rec.source.imageUrls?.[0];
  const pick = async (m: ChooserMockup) => {
    if (busy) return;
    if (!mockupHasArea(m)) { setMsg(`"${m.label}" mockupında baskı alanı yok; önce alanını belirle.`); return; }
    setBusy(m.id); setMsg(`"${m.label}" örneklerle basılıyor…`);
    try {
      await customApi("POST", `/${rec.id}/images`, { mockupIds: [m.id], reserve });
      setAdded((a) => [...a, m.id]); setMsg(`"${m.label}" galeriye eklendi.`); onAdded();
    } catch (e) { setMsg((e as Error).message); }
    setBusy(null);
  };
  return (
    <>
      <MockupChooserModal
        title="Mockup ekle — tıkladığın mockup örneklerle basılıp galerinin sonuna eklenir"
        mode="multi" newestFirst selectedIds={added}
        inspiration={insp ? { imageUrl: insp, title: rec.name } : null}
        onPick={(m) => void pick(m)}
        onClose={onClose}
        apparel={rec.isGarment !== false}
        {...studioTestDesigns(rec)}
      />
      {msg && <div className="fixed bottom-4 left-1/2 z-[70] -translate-x-1/2 rounded-full bg-gray-900 px-4 py-2 text-sm text-white shadow-lg">{busy && <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />}{msg}</div>}
    </>
  );
}
